import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store from '../state';
import maplibregl from 'maplibre-gl';

export class FireRestrictionsLayer extends BaseProduct {
  public readonly id: ProductId = 'fire_restrictions';
  private pinnedPopup: maplibregl.Popup | null = null;
  private hoverPopup: maplibregl.Popup | null = null;
  private mapEventsBound: boolean = false;
  private validAsOfTimestamp: string | null = null;

  protected onActivate(): void {
    this.refresh();
    this.bindMapEvents();
  }

  protected onDeactivate(): void {
    this.removeLayers();
    this.unbindMapEvents();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh() {
    if (!this.active) return;
    this.setupLayers();
    this.updateVisibility();
  }

  private setupLayers() {
    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = "https://auth.skyskout.com/";
    } else if (apiServer === "") {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const versionParam = manifest?.generated_at ? `?v=${encodeURIComponent(manifest.generated_at)}` : `?t=${Date.now()}`;
    const dataUrl = `${apiServer}${tileRoot}/observed/fire_restrictions/fire_restrictions.geojson${versionParam}`;

    // Fetch root metadata (valid_as_of timestamp)
    fetch(dataUrl)
      .then(async res => {
        if (!res.ok) return;
        const text = await res.text();
        const match = text.match(/"valid_as_of"\s*:\s*"([^"]+)"/);
        if (match) {
          this.validAsOfTimestamp = match[1];
        }
      })
      .catch(err => {
        console.warn('⚠️ Could not fetch fire restrictions valid_as_of metadata:', err);
      });

    // Add or update Fire Restrictions GeoJSON Source
    if (!this.map.getSource('fire-restrictions-source')) {
      this.map.addSource('fire-restrictions-source', {
        type: 'geojson',
        data: dataUrl
      });
    } else {
      const source = this.map.getSource('fire-restrictions-source') as maplibregl.GeoJSONSource;
      if (source && typeof source.setData === 'function') {
        source.setData(dataUrl);
      }
    }

    // Dynamic layer ordering: insert dynamic overlays beneath city labels
    const layers = this.map.getStyle()?.layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    // 1. Fill Layer: Color matched by restriction stage
    if (!this.map.getLayer('fire-restrictions-fill')) {
      this.map.addLayer({
        id: 'fire-restrictions-fill',
        type: 'fill',
        source: 'fire-restrictions-source',
        paint: {
          'fill-color': [
            'match',
            ['get', 'stage'],
            1, '#f59e0b', // Stage 1: Amber
            2, '#ea580c', // Stage 2: Orange
            3, '#dc2626', // Stage 3 / Closure: Red
            /* fallback / Stage 0 (No Restrictions) */ '#10b981' // Subtle Green
          ],
          'fill-opacity': [
            'match',
            ['get', 'stage'],
            1, 0.28,
            2, 0.38,
            3, 0.52,
            /* fallback / Stage 0 (No Restrictions) */ 0.10
          ]
        }
      }, beforeId);
    }

    // 2. Stroke Background (White outline halo)
    if (!this.map.getLayer('fire-restrictions-stroke-bg')) {
      this.map.addLayer({
        id: 'fire-restrictions-stroke-bg',
        type: 'line',
        source: 'fire-restrictions-source',
        paint: {
          'line-color': '#ffffff',
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 2.5, 8, 4.0, 14, 6.0],
          'line-opacity': 0.7
        }
      }, beforeId);
    }

    // 3. Stroke Foreground (Stage colored dashed border)
    if (!this.map.getLayer('fire-restrictions-stroke-fg')) {
      this.map.addLayer({
        id: 'fire-restrictions-stroke-fg',
        type: 'line',
        source: 'fire-restrictions-source',
        paint: {
          'line-color': [
            'match',
            ['get', 'stage'],
            1, '#f59e0b',
            2, '#ea580c',
            3, '#dc2626',
            /* fallback / Stage 0 (No Restrictions) */ '#10b981'
          ],
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 1.5, 8, 2.5, 14, 4.0],
          'line-dasharray': [4, 2]
        }
      }, beforeId);
    }

    // 4. Symbol Layer: Unit name + Stage
    if (!this.map.getLayer('fire-restrictions-label')) {
      this.map.addLayer({
        id: 'fire-restrictions-label',
        type: 'symbol',
        source: 'fire-restrictions-source',
        minzoom: 6,
        layout: {
          'text-field': ['get', 'unit_name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 10, 13, 14, 15],
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 2.0
        }
      }, beforeId);
    }
  }

  private updateVisibility() {
    const isVisible = true;
    ['fire-restrictions-fill', 'fire-restrictions-stroke-bg', 'fire-restrictions-stroke-fg', 'fire-restrictions-label'].forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
      }
    });
  }

  private removeLayers() {
    ['fire-restrictions-label', 'fire-restrictions-stroke-fg', 'fire-restrictions-stroke-bg', 'fire-restrictions-fill'].forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });
    if (this.map.getSource('fire-restrictions-source')) {
      this.map.removeSource('fire-restrictions-source');
    }
  }

  private bindMapEvents() {
    if (this.mapEventsBound) return;
    this.mapEventsBound = true;

    this.hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'gw-fire-restrictions-popup',
      maxWidth: '380px'
    });

    ['fire-restrictions-fill'].forEach(layerId => {
      this.map.on('mouseenter', layerId, this.handleMouseEnter);
      this.map.on('mouseleave', layerId, this.handleMouseLeave);
      this.map.on('click', layerId, this.handleClick);
    });
  }

  private unbindMapEvents() {
    if (!this.mapEventsBound) return;
    this.mapEventsBound = false;

    ['fire-restrictions-fill'].forEach(layerId => {
      this.map.off('mouseenter', layerId, this.handleMouseEnter);
      this.map.off('mouseleave', layerId, this.handleMouseLeave);
      this.map.off('click', layerId, this.handleClick);
    });

    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
      this.pinnedPopup = null;
    }
  }

  private getPriorityFeature(features?: maplibregl.MapGeoJSONFeature[]): Record<string, any> {
    if (!features || features.length === 0) return {};
    const sorted = [...features].sort((a, b) => {
      const agencyA = a.properties?.agency || '';
      const agencyB = b.properties?.agency || '';
      const isSpecificA = agencyA.includes('NPS') || agencyA.includes('USFS') || agencyA.includes('BIA') || agencyA.toLowerCase().includes('tribal');
      const isSpecificB = agencyB.includes('NPS') || agencyB.includes('USFS') || agencyB.includes('BIA') || agencyB.toLowerCase().includes('tribal');

      const rawStageA = a.properties?.stage !== undefined && a.properties?.stage !== null ? Number(a.properties.stage) : 0;
      const rawStageB = b.properties?.stage !== undefined && b.properties?.stage !== null ? Number(b.properties.stage) : 0;
      const stageA = Math.max(0, rawStageA);
      const stageB = Math.max(0, rawStageB);

      // 1. Higher restriction stage always takes precedence over No Restrictions (0)
      if (stageB !== stageA) return stageB - stageA;

      // 2. Active feed parcels with an effective date or order number take precedence over baseline boundaries
      const hasOrderA = Boolean(a.properties?.order_num || a.properties?.effective_date);
      const hasOrderB = Boolean(b.properties?.order_num || b.properties?.effective_date);
      if (hasOrderA && !hasOrderB) return -1;
      if (hasOrderB && !hasOrderA) return 1;

      // 3. Specific land unit agencies (NPS, USFS, BIA) take priority over broad BLM district administrative envelopes
      if (isSpecificA && !isSpecificB) return -1;
      if (isSpecificB && !isSpecificA) return 1;

      return 0;
    });
    return sorted[0].properties || {};
  }

  private handleMouseEnter = (e: maplibregl.MapLayerMouseEvent) => {
    this.map.getCanvas().style.cursor = 'pointer';
    // If a popup is already pinned open by a user click, don't show the hover popup
    if (this.pinnedPopup && this.pinnedPopup.isOpen()) return;
    if (!e.features || e.features.length === 0 || !this.hoverPopup) return;

    const p = this.getPriorityFeature(e.features);
    const html = this.buildPopupContent(p, false);

    this.hoverPopup
      .setLngLat(e.lngLat)
      .setHTML(html)
      .addTo(this.map);
  };

  private handleMouseLeave = () => {
    this.map.getCanvas().style.cursor = '';
    // Only dismiss the hover tooltip; keep any pinned click popup active
    if (this.hoverPopup) {
      this.hoverPopup.remove();
    }
  };

  private handleClick = (e: maplibregl.MapLayerMouseEvent) => {
    if (!e.features || e.features.length === 0) return;

    // Remove any hover preview
    if (this.hoverPopup) {
      this.hoverPopup.remove();
    }

    // Dismiss existing pinned popup if open
    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
    }

    const p = this.getPriorityFeature(e.features);
    const html = this.buildPopupContent(p, true);

    this.pinnedPopup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      closeOnMove: false,
      className: 'gw-fire-restrictions-popup gw-fire-restrictions-pinned',
      maxWidth: '380px'
    })
      .setLngLat(e.lngLat)
      .setHTML(html)
      .addTo(this.map);

    this.pinnedPopup.on('close', () => {
      this.pinnedPopup = null;
    });
  };

  private buildPopupContent(p: Record<string, any>, isPinned: boolean = false): string {
    const rawStage = p.stage !== undefined && p.stage !== null ? Number(p.stage) : 0;
    const stage = rawStage < 0 ? 0 : rawStage;
    const unitName = p.unit_name || p.name || p.UNIT_NAME || p.forestname || p.PARENT_NAME || 'Public Land Region';
    const agency = p.agency || 'Federal Land';
    const stageLabel = (p.stage_label && p.stage_label !== 'No Data Available')
      ? p.stage_label
      : (stage === 0 ? 'No Restrictions' : `Stage ${stage} Restrictions`);
    const orderUrl = p.order_url || '';
    const orderNum = p.order_num || '';
    const notes = p.notes || '';

    // Stage header colors & icons (Fallback / Stage 0: No Restrictions)
    let stageColor = '#10b981';
    let stageIcon = '✅';
    let stageBadgeBg = 'rgba(16, 185, 129, 0.2)';
    let stageBorder = 'rgba(16, 185, 129, 0.4)';

    if (stage === 1) {
      stageColor = '#f59e0b';
      stageIcon = '🟡';
      stageBadgeBg = 'rgba(245, 158, 11, 0.2)';
      stageBorder = 'rgba(245, 158, 11, 0.4)';
    } else if (stage === 2) {
      stageColor = '#ea580c';
      stageIcon = '🟠';
      stageBadgeBg = 'rgba(234, 88, 12, 0.2)';
      stageBorder = 'rgba(234, 88, 12, 0.4)';
    } else if (stage >= 3) {
      stageColor = '#dc2626';
      stageIcon = '🔴';
      stageBadgeBg = 'rgba(220, 38, 38, 0.2)';
      stageBorder = 'rgba(220, 38, 38, 0.4)';
    }

    // Agency badge
    let agencyIcon = '🌲';
    if (agency.includes('BLM')) agencyIcon = '🏜️';
    else if (agency.includes('NPS')) agencyIcon = '🏞️';
    else if (agency.includes('BIA') || agency.includes('Tribal')) agencyIcon = '🏕️';

    // Activity permission helper (fallback to allowed when no restriction order is active)
    const getBadge = (rule: string, okLabel = 'Allowed', warnLabel = 'Restricted', badLabel = 'Prohibited') => {
      if (rule === 'allowed' || rule === 'unknown' || !rule) {
        return `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border-radius: 4px; font-weight: 600; font-size: 11px; background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.3);">✅ ${okLabel}</span>`;
      } else if (rule === 'developed_only' || rule === 'enclosed_only') {
        return `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border-radius: 4px; font-weight: 600; font-size: 11px; background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.3);">⚠️ ${warnLabel}</span>`;
      } else {
        return `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border-radius: 4px; font-weight: 600; font-size: 11px; background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.3);">❌ ${badLabel}</span>`;
      }
    };

    const resolveRule = (propVal: string | undefined, defaultForStage: string) => {
      if (!propVal || propVal === 'unknown') return defaultForStage;
      return propVal;
    };

    const propaneBadge = getBadge(resolveRule(p.propane_stoves, stage >= 3 ? 'prohibited' : 'allowed'), 'Allowed (with shutoff valve)', 'Restricted', 'Prohibited');
    const developedCampfireBadge = getBadge(resolveRule(p.campfire_developed_rings, stage <= 1 ? 'allowed' : 'prohibited'), 'Allowed in official metal rings', 'Restricted', 'Banned');
    const dispersedCampfireBadge = getBadge(resolveRule(p.campfire_dispersed, stage === 0 ? 'allowed' : 'prohibited'), 'Allowed', 'Restricted', 'Banned');
    const charcoalBadge = getBadge(resolveRule(p.charcoal, stage === 0 ? 'allowed' : stage === 1 ? 'developed_only' : 'prohibited'), 'Allowed', 'Developed Sites Only', 'Banned');
    const smokingBadge = getBadge(resolveRule(p.smoking, stage === 0 ? 'allowed' : stage <= 2 ? 'enclosed_only' : 'prohibited'), 'Allowed', 'Enclosed / Cleared Only', 'Banned');

    const sourceUrl = p.source_url || p.order_url || '';
    const acquiredAt = p.acquired_at || this.validAsOfTimestamp;

    let acquiredTimeStr = '';
    if (acquiredAt) {
      try {
        acquiredTimeStr = new Date(acquiredAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
      } catch {
        acquiredTimeStr = acquiredAt;
      }
    }

    const agencyText = agency.toLowerCase().includes('managed') ? agency : `${agency} Managed Region`;

    return `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #f8fafc; padding: 2px 0;">
        <div style="font-size: 16px; font-weight: 800; color: ${stageColor}; line-height: 1.25; margin-bottom: 6px; padding-right: 20px;">
          ${unitName}${p.state ? `<span style="font-size: 11px; font-weight: 500; color: #94a3b8; margin-left: 6px;">(${p.state})</span>` : ''}
        </div>

        <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid rgba(255, 255, 255, 0.1);">
          <span style="font-size: 11px; font-weight: 700; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.5px; display: inline-flex; align-items: center; gap: 4px;">
            ${agencyIcon} ${agencyText}
          </span>
          <span style="font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 9999px; background: ${stageBadgeBg}; color: ${stageColor}; border: 1px solid ${stageBorder};">
            ${stageIcon} ${stageLabel}
          </span>
        </div>

        <div style="background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 8px; padding: 8px 10px; margin-bottom: 10px; display: flex; flex-direction: column; gap: 7px;">
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px;">
            <span style="color: #cbd5e1;">🍳 Propane / Gas Stoves:</span>
            ${propaneBadge}
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px;">
            <span style="color: #cbd5e1;">🏕️ Developed Campground Fires:</span>
            ${developedCampfireBadge}
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px;">
            <span style="color: #cbd5e1;">🪵 Backcountry / Dispersed Fires:</span>
            ${dispersedCampfireBadge}
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px;">
            <span style="color: #cbd5e1;">🍖 Charcoal / Wood Grills:</span>
            ${charcoalBadge}
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px;">
            <span style="color: #cbd5e1;">🚬 Smoking Outdoors:</span>
            ${smokingBadge}
          </div>
        </div>

        ${(orderUrl || sourceUrl) ? `
          <a href="${orderUrl || sourceUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; margin-bottom: 10px; border-radius: 6px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); text-decoration: none; font-size: 12px; font-weight: 700; text-align: center;">
            🔗 View Official Agency Orders & Conditions ↗
          </a>
        ` : ''}

        ${orderNum ? `<div style="font-size: 11px; color: #94a3b8; margin-bottom: 4px;">Order #: <span style="color: #e2e8f0; font-family: monospace;">${orderNum}</span></div>` : ''}
        ${notes && notes.length > 3 ? `<div style="font-size: 11px; color: #cbd5e1; font-style: italic; margin-bottom: 6px; line-height: 1.3;">"${notes.slice(0, 160)}${notes.length > 160 ? '...' : ''}"</div>` : ''}

        <div style="font-size: 10px; color: #94a3b8; background: rgba(255, 255, 255, 0.04); border-radius: 6px; padding: 5px 8px; margin-bottom: 8px; line-height: 1.35;">
          ⚠️ <em>Restrictions change frequently. Always check official agency orders before lighting fires or using stoves.</em>
        </div>

        <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-top: 4px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.08); font-size: 10px; color: #64748b;">
          <span>${acquiredTimeStr ? `Acquired: ${acquiredTimeStr}` : 'Federal Land Source'}</span>
          <div style="display: flex; gap: 8px;">
            ${orderUrl ? `<a href="${orderUrl}" target="_blank" rel="noopener noreferrer" style="color: #38bdf8; text-decoration: none; font-weight: 600; display: inline-flex; align-items: center; gap: 2px;">Official Orders / Info ↗</a>` : ''}
            ${sourceUrl && sourceUrl !== orderUrl ? `<a href="${sourceUrl}" target="_blank" rel="noopener noreferrer" style="color: #60a5fa; text-decoration: none; font-weight: 600; display: inline-flex; align-items: center; gap: 2px;">Agency Site ↗</a>` : ''}
          </div>
        </div>

        ${!isPinned ? `
          <div style="font-size: 10px; color: #38bdf8; text-align: center; margin-top: 6px; padding: 3px 6px; background: rgba(56, 189, 248, 0.1); border: 1px dashed rgba(56, 189, 248, 0.3); border-radius: 4px; font-weight: 500;">
            👆 Click area to pin card & click links
          </div>
        ` : ''}
      </div>
    `;
  }
}

export default FireRestrictionsLayer;
